import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Save, ToggleLeft, ToggleRight, UtensilsCrossed, Video, Image as ImageIcon, Upload, Trash2, Flame } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LANDING_IMAGE_SLOTS = [
  { key: 'hero_video_bg', label: 'Fundo do vídeo (placeholder)', desc: 'Aparece atrás do "play" quando não há vídeo configurado.' },
  { key: 'thiago_photo', label: 'Foto do Thiago Ferraz', desc: 'Seção "Nada de treino genérico" — retrato 3:4.' },
  { key: 'community_photo', label: 'Foto da comunidade', desc: 'Seção "Ninguém evolui sozinho" — quadrada.' },
];

const LandingImageSlot = ({ slot, url, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem('token');
  const previewUrl = url ? (url.startsWith('http') ? url : `${API}${url}`) : '';

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Use JPG, PNG ou WebP');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Máximo 8MB');
      return;
    }
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/api/admin/settings/landing-images/${slot.key}`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onChange(res.data);
      toast.success(`${slot.label} atualizada!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro no upload');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleReset = async () => {
    try {
      const res = await axios.delete(`${API}/api/admin/settings/landing-images/${slot.key}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onChange(res.data);
      toast.success('Imagem removida (voltou para o padrão)');
    } catch {
      toast.error('Erro ao remover');
    }
  };

  return (
    <div className="p-4 bg-zinc-950/50 rounded-xl border border-zinc-800" data-testid={`landing-img-slot-${slot.key}`}>
      <div className="flex gap-4">
        <div className="w-24 h-24 rounded-lg bg-zinc-900 border border-zinc-800 overflow-hidden flex-shrink-0">
          {previewUrl ? (
            <img src={previewUrl} alt={slot.label} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-zinc-700" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-bold">{slot.label}</p>
          <p className="text-zinc-500 text-xs mb-3">{slot.desc}</p>
          <div className="flex flex-wrap gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFile}
              className="hidden"
              data-testid={`landing-img-input-${slot.key}`}
            />
            <Button
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="bg-neon-lime text-black font-bold hover:bg-neon-lime/80 h-8"
              data-testid={`landing-img-upload-${slot.key}`}
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {uploading ? 'Enviando...' : (url ? 'Trocar' : 'Enviar foto')}
            </Button>
            {url && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleReset}
                className="text-zinc-400 hover:text-red-400 h-8"
                data-testid={`landing-img-reset-${slot.key}`}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Padrão
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SettingsTab = () => {
  const [joinButton, setJoinButton] = useState({
    enabled: false,
    text: 'Quero participar',
    url: '',
    open_new_tab: true
  });
  const [macroflex, setMacroflex] = useState({
    enabled: false,
    url: 'https://www.macroflex.com.br',
    label: 'Dieta'
  });
  const [saving, setSaving] = useState(false);
  const [savingMacro, setSavingMacro] = useState(false);
  const [landingVideoUrl, setLandingVideoUrl] = useState('');
  const [savingVideo, setSavingVideo] = useState(false);
  const [landingImages, setLandingImages] = useState({ hero_video_bg: '', thiago_photo: '', community_photo: '' });
  const [gallery, setGallery] = useState([]);
  const [galleryMax, setGalleryMax] = useState(20);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const galleryInputRef = useRef(null);
  const [scarcity, setScarcity] = useState({ enabled: false, spots: 10, show_countdown: true });
  const [savingScarcity, setSavingScarcity] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/settings/join-button`)
      .then(res => setJoinButton(res.data))
      .catch(() => {});
    axios.get(`${API}/api/settings/macroflex`)
      .then(res => setMacroflex(res.data))
      .catch(() => {});
    axios.get(`${API}/api/settings/landing-video`)
      .then(res => setLandingVideoUrl(res.data?.url || ''))
      .catch(() => {});
    axios.get(`${API}/api/settings/landing-images`)
      .then(res => setLandingImages({
        hero_video_bg: res.data?.hero_video_bg || '',
        thiago_photo: res.data?.thiago_photo || '',
        community_photo: res.data?.community_photo || '',
      }))
      .catch(() => {});
    axios.get(`${API}/api/settings/landing-gallery`)
      .then(res => {
        setGallery(res.data?.photos || []);
        setGalleryMax(res.data?.max || 20);
      })
      .catch(() => {});
    axios.get(`${API}/api/settings/landing-scarcity`)
      .then(res => setScarcity({
        enabled: !!res.data?.enabled,
        spots: Number(res.data?.spots) || 10,
        show_countdown: res.data?.show_countdown !== false,
      }))
      .catch(() => {});
  }, []);

  const handleSaveScarcity = async () => {
    const token = localStorage.getItem('token');
    setSavingScarcity(true);
    try {
      const res = await axios.put(`${API}/api/admin/settings/landing-scarcity`, scarcity, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScarcity({
        enabled: !!res.data?.enabled,
        spots: Number(res.data?.spots) || 10,
        show_countdown: res.data?.show_countdown !== false,
      });
      toast.success('Contador de escassez atualizado!');
    } catch {
      toast.error('Erro ao salvar');
    } finally {
      setSavingScarcity(false);
    }
  };

  const handleSaveVideo = async () => {
    setSavingVideo(true);
    try {
      await axios.put(`${API}/api/admin/settings/landing-video`, { url: landingVideoUrl });
      toast.success('Vídeo da landing page atualizado!');
    } catch {
      toast.error('Erro ao salvar vídeo');
    } finally {
      setSavingVideo(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const token = localStorage.getItem('token');
    setGalleryUploading(true);
    let added = 0;
    let failed = 0;
    for (const file of files) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        failed++;
        continue;
      }
      if (file.size > 8 * 1024 * 1024) {
        failed++;
        continue;
      }
      const form = new FormData();
      form.append('file', file);
      try {
        const res = await axios.post(`${API}/api/admin/settings/landing-gallery`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setGallery(prev => [...prev, res.data]);
        added++;
      } catch (err) {
        failed++;
        if (err?.response?.status === 400 && err?.response?.data?.detail?.includes('Limite')) {
          toast.error(err.response.data.detail);
          break;
        }
      }
    }
    setGalleryUploading(false);
    if (galleryInputRef.current) galleryInputRef.current.value = '';
    if (added > 0) toast.success(`${added} foto(s) adicionada(s) à Wall of Fame`);
    if (failed > 0) toast.error(`${failed} foto(s) falharam (formato/tamanho inválido)`);
  };

  const handleGalleryDelete = async (photoId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/api/admin/settings/landing-gallery/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGallery(prev => prev.filter(p => p.id !== photoId));
      toast.success('Foto removida');
    } catch {
      toast.error('Erro ao remover foto');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/admin/settings/join-button`, joinButton);
      setJoinButton(res.data);
      toast.success('Configuração salva!');
    } catch {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMacroflex = async () => {
    setSavingMacro(true);
    try {
      const res = await axios.put(`${API}/api/admin/settings/macroflex`, macroflex);
      setMacroflex(res.data);
      toast.success('Macroflex atualizado!');
    } catch {
      toast.error('Erro ao salvar');
    } finally {
      setSavingMacro(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Landing page video */}
      <motion.div
        className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="landing-video-card"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-neon-lime/10 flex items-center justify-center">
            <Video className="w-5 h-5 text-neon-lime" />
          </div>
          <div>
            <h3 className="text-white font-bold">Vídeo da Landing Page</h3>
            <p className="text-zinc-500 text-xs">Cole o link do YouTube que aparecerá na página de vendas</p>
          </div>
        </div>
        <Input
          type="url"
          value={landingVideoUrl}
          onChange={(e) => setLandingVideoUrl(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=..."
          className="bg-zinc-950 border-zinc-800 text-white mb-3"
          data-testid="landing-video-input"
        />
        <Button
          onClick={handleSaveVideo}
          disabled={savingVideo}
          className="w-full bg-neon-lime text-black font-black hover:bg-neon-lime/80"
          data-testid="landing-video-save"
        >
          <Save className="w-4 h-4 mr-2" />
          {savingVideo ? 'Salvando...' : 'Salvar vídeo'}
        </Button>
        {landingVideoUrl && (
          <p className="text-zinc-500 text-xs mt-2">Link atual: <a href={landingVideoUrl} target="_blank" rel="noopener noreferrer" className="text-neon-lime hover:underline">{landingVideoUrl}</a></p>
        )}
      </motion.div>

      {/* Landing page images */}
      <motion.div
        className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        data-testid="landing-images-card"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-neon-lime/10 flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-neon-lime" />
          </div>
          <div>
            <h3 className="text-white font-bold">Imagens da Landing Page</h3>
            <p className="text-zinc-500 text-xs">Suba fotos reais dos alunos e do Thiago. JPG/PNG/WebP até 8MB.</p>
          </div>
        </div>
        <div className="space-y-3">
          {LANDING_IMAGE_SLOTS.map((slot) => (
            <LandingImageSlot
              key={slot.key}
              slot={slot}
              url={landingImages[slot.key]}
              onChange={(data) => setLandingImages({
                hero_video_bg: data?.hero_video_bg || '',
                thiago_photo: data?.thiago_photo || '',
                community_photo: data?.community_photo || '',
              })}
            />
          ))}
        </div>
      </motion.div>

      {/* Wall of Fame - Landing Gallery */}
      <motion.div
        className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08 }}
        data-testid="landing-gallery-card"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-neon-lime/10 flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-neon-lime" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-bold">Wall of Fame — Fotos dos alunos</h3>
            <p className="text-zinc-500 text-xs">Carrossel rotativo na landing. Até {galleryMax} fotos.</p>
          </div>
          <div className="text-right">
            <p className="text-neon-lime font-black text-lg leading-none">{gallery.length}<span className="text-zinc-600 text-sm font-bold">/{galleryMax}</span></p>
          </div>
        </div>

        <input
          ref={galleryInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={handleGalleryUpload}
          className="hidden"
          data-testid="gallery-file-input"
        />
        <Button
          onClick={() => galleryInputRef.current?.click()}
          disabled={galleryUploading || gallery.length >= galleryMax}
          className="w-full bg-neon-lime text-black font-black hover:bg-neon-lime/80 mb-4"
          data-testid="gallery-upload-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          {galleryUploading ? 'Enviando...' : (gallery.length >= galleryMax ? 'Limite atingido' : 'Adicionar fotos (múltipla seleção)')}
        </Button>

        {gallery.length === 0 ? (
          <div className="text-center py-8 text-zinc-500 text-sm border border-dashed border-zinc-800 rounded-xl">
            Nenhuma foto ainda. Suba as primeiras para ativar a Wall of Fame na landing.
          </div>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2" data-testid="gallery-grid">
            {gallery.map((photo) => (
              <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden border border-zinc-800 bg-zinc-900">
                <img
                  src={photo.url.startsWith('http') ? photo.url : `${API}${photo.url}`}
                  alt="Aluno"
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => handleGalleryDelete(photo.id)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-500 transition-all"
                  data-testid={`gallery-delete-${photo.id}`}
                  title="Remover"
                >
                  <Trash2 className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Scarcity / Urgency counter */}
      <motion.div
        className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        data-testid="scarcity-card"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
            <Flame className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h3 className="text-white font-bold">Contador de escassez</h3>
            <p className="text-zinc-500 text-xs">Aparece embaixo do CTA principal — "Últimas X vagas" + countdown até meia-noite</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-xl border border-zinc-800">
            <span className="text-zinc-300 text-sm font-medium">Contador ativo na landing</span>
            <button
              onClick={() => setScarcity(prev => ({ ...prev, enabled: !prev.enabled }))}
              className="text-neon-lime"
              data-testid="scarcity-enabled-toggle"
            >
              {scarcity.enabled ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8 text-zinc-600" />}
            </button>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-1.5">Vagas restantes</label>
            <Input
              type="number"
              min="0"
              max="999"
              value={scarcity.spots}
              onChange={(e) => setScarcity(prev => ({ ...prev, spots: Number(e.target.value) || 0 }))}
              className="bg-zinc-950 border-zinc-800 text-white"
              data-testid="scarcity-spots-input"
            />
            <p className="text-xs text-zinc-600 mt-1">Você controla esse número. Vai aparecer como "Últimas {scarcity.spots} vagas"</p>
          </div>

          <div className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-xl border border-zinc-800">
            <span className="text-zinc-300 text-sm font-medium">Mostrar countdown até meia-noite</span>
            <button
              onClick={() => setScarcity(prev => ({ ...prev, show_countdown: !prev.show_countdown }))}
              className="text-neon-lime"
              data-testid="scarcity-countdown-toggle"
            >
              {scarcity.show_countdown ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8 text-zinc-600" />}
            </button>
          </div>

          {scarcity.enabled && (
            <div className="p-4 bg-zinc-950 rounded-xl border border-zinc-800">
              <p className="text-xs text-zinc-500 mb-3 uppercase tracking-wider">Preview</p>
              <div className="inline-flex flex-wrap items-center gap-3 rounded-full border px-4 py-2.5" style={{ borderColor: 'rgba(239,68,68,0.4)', background: 'linear-gradient(90deg, rgba(239,68,68,0.12), rgba(212,175,55,0.08))' }}>
                <span className="relative flex items-center justify-center w-2.5 h-2.5">
                  <span className="absolute inset-0 rounded-full bg-red-500 opacity-70 animate-ping" />
                  <span className="relative w-2.5 h-2.5 rounded-full bg-red-500" />
                </span>
                <span className="font-black uppercase tracking-wider text-white text-xs">
                  Últimas <span className="text-red-400">{scarcity.spots}</span> vagas
                </span>
                {scarcity.show_countdown && (
                  <>
                    <span className="w-px h-3 bg-white/20" />
                    <span className="text-zinc-300 text-xs">
                      Oferta expira em <span className="font-mono font-black text-neon-lime">23:59:59</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          <Button
            onClick={handleSaveScarcity}
            disabled={savingScarcity}
            className="w-full bg-red-500 text-white font-black hover:bg-red-600"
            data-testid="scarcity-save"
          >
            <Save className="w-4 h-4 mr-2" />
            {savingScarcity ? 'Salvando...' : 'Salvar contador'}
          </Button>
        </div>
      </motion.div>

      <motion.div
        className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-neon-lime/10 flex items-center justify-center">
            <ExternalLink className="w-5 h-5 text-neon-lime" />
          </div>
          <div>
            <h3 className="font-bold text-white text-lg">Botão "Quero Participar"</h3>
            <p className="text-zinc-500 text-sm">Aparece na página de login para novos interessados</p>
          </div>
        </div>

        <div className="space-y-5">
          {/* Toggle enabled */}
          <div className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-xl border border-zinc-800">
            <span className="text-zinc-300 text-sm font-medium">Botão ativo</span>
            <button
              onClick={() => setJoinButton(prev => ({ ...prev, enabled: !prev.enabled }))}
              className="text-neon-lime"
              data-testid="join-btn-toggle"
            >
              {joinButton.enabled
                ? <ToggleRight className="w-8 h-8" />
                : <ToggleLeft className="w-8 h-8 text-zinc-600" />
              }
            </button>
          </div>

          {/* Text */}
          <div>
            <label className="block text-sm text-zinc-400 mb-1.5">Texto do botão</label>
            <Input
              value={joinButton.text}
              onChange={(e) => setJoinButton(prev => ({ ...prev, text: e.target.value }))}
              placeholder="Ex: Quero participar"
              className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white"
              data-testid="join-btn-text"
            />
          </div>

          {/* URL */}
          <div>
            <label className="block text-sm text-zinc-400 mb-1.5">Link de destino</label>
            <Input
              value={joinButton.url}
              onChange={(e) => setJoinButton(prev => ({ ...prev, url: e.target.value }))}
              placeholder="https://..."
              className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white"
              data-testid="join-btn-url"
            />
            <p className="text-xs text-zinc-600 mt-1">Cole aqui o link do formulário, WhatsApp, etc.</p>
          </div>

          {/* Open new tab toggle */}
          <div className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-xl border border-zinc-800">
            <span className="text-zinc-300 text-sm font-medium">Abrir em nova aba</span>
            <button
              onClick={() => setJoinButton(prev => ({ ...prev, open_new_tab: !prev.open_new_tab }))}
              className="text-neon-lime"
              data-testid="join-btn-newtab"
            >
              {joinButton.open_new_tab
                ? <ToggleRight className="w-8 h-8" />
                : <ToggleLeft className="w-8 h-8 text-zinc-600" />
              }
            </button>
          </div>

          {/* Preview */}
          {joinButton.enabled && joinButton.url && (
            <div className="p-4 bg-zinc-950 rounded-xl border border-zinc-800">
              <p className="text-xs text-zinc-500 mb-3 uppercase tracking-wider">Preview</p>
              <div className="w-full h-12 border-2 border-neon-lime rounded-lg flex items-center justify-center text-neon-lime font-black uppercase tracking-wider text-sm">
                {joinButton.text || 'Quero participar'}
              </div>
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-neon-lime text-black font-bold hover:bg-neon-lime/80 h-11"
            data-testid="join-btn-save"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvando...' : 'Salvar Configuração'}
          </Button>
        </div>
      </motion.div>

      {/* Macroflex */}
      <motion.div
        className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-neon-purple/10 flex items-center justify-center">
            <UtensilsCrossed className="w-5 h-5 text-neon-purple" />
          </div>
          <div>
            <h3 className="font-bold text-white text-lg">Macroflex / Nutrição</h3>
            <p className="text-zinc-500 text-sm">Link externo na barra inferior do app</p>
          </div>
        </div>

        <div className="space-y-5">
          <div className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-xl border border-zinc-800">
            <span className="text-zinc-300 text-sm font-medium">Visível para alunos</span>
            <button
              onClick={() => setMacroflex(prev => ({ ...prev, enabled: !prev.enabled }))}
              className="text-neon-purple"
              data-testid="macroflex-toggle"
            >
              {macroflex.enabled
                ? <ToggleRight className="w-8 h-8" />
                : <ToggleLeft className="w-8 h-8 text-zinc-600" />
              }
            </button>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-1.5">Nome do botão</label>
            <Input
              value={macroflex.label}
              onChange={(e) => setMacroflex(prev => ({ ...prev, label: e.target.value }))}
              placeholder="Ex: Dieta, Nutrição, Macroflex"
              className="bg-zinc-950/50 border-zinc-800 focus:border-neon-purple text-white"
              data-testid="macroflex-label"
            />
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-1.5">URL do Macroflex</label>
            <Input
              value={macroflex.url}
              onChange={(e) => setMacroflex(prev => ({ ...prev, url: e.target.value }))}
              placeholder="https://www.macroflex.com.br"
              className="bg-zinc-950/50 border-zinc-800 focus:border-neon-purple text-white"
              data-testid="macroflex-url"
            />
            <p className="text-xs text-zinc-600 mt-1">O aluno será redirecionado para este link ao clicar</p>
          </div>

          <Button
            onClick={handleSaveMacroflex}
            disabled={savingMacro}
            className="w-full bg-neon-purple text-white font-bold hover:bg-neon-purple/80 h-11"
            data-testid="macroflex-save"
          >
            <Save className="w-4 h-4 mr-2" />
            {savingMacro ? 'Salvando...' : 'Salvar Macroflex'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default SettingsTab;
